// src/main.js - Mini Agents $MINI - Main Game Controller
// ============================================================================
// This file manages the game loop, character minting, wallet connection,
// and all agent interactions for the Mini Agents platform.
// ============================================================================

import './style.css'
import { Map } from './game/map.js'
import { Character, STOP, MOVE } from './game/character.js'
import { calcOffset, div, WIDTH, HEIGHT, GS, LEFT, RIGHT, UP, DOWN } from './game/utility.js'
import { 
  connectWallet, 
  getWalletInfo, 
  generateMockWalletAddress,
  saveWorkersForUser,
  loadWorkersForUser,
  userWallet 
} from './services/wallet.js'

// ============================================================================
// AGENT IMPORTS
// Add your custom agent import here following this pattern:
// import { handleYourAgentMessage } from './agents/your_agent.js'
// ============================================================================
import { handleCygaarMessage } from './agents/cygaar.js'
import { handleSoldierMessage } from './agents/soldier.js'
import { handleMinisterMessage } from './agents/minister.js'
import { handleInspectorMessage } from './agents/inspector.js'
import { handlePhinMessage } from './agents/phin.js'
import { handleAbsterMessage } from './agents/abster.js'
import { handleGuruMessage } from './agents/guru.js'
import { handleAnalyzerMessage } from './agents/analyzer.js'
import { handleRickMessage } from './agents/rick.js'
import { handleRoseMessage } from './agents/rose.js'

// ============================================================================
// GLOBAL VARIABLES
// ============================================================================
let map;
let offset = [0, 0];
let npcCounter = 0;

// ============================================================================
// MINTED AGENTS TRACKER
// Add your custom agent here: your_agent_name: false
// ============================================================================
let mintedWorkers = {
  cygaar: false,
  minister: false,
  soldier: false,
  inspector: false,
  phin: false,
  abster: false,
  guru: false,
  analyzer: false,
  rick: false,
  rose: false
  // Add your custom agent here:
  // your_agent: false
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Logs a message to the in-game console terminal
 * @param {string} text - The message to display
 */
function logToConsole(text) {
  const log = document.getElementById('consoleLog');
  const line = document.createElement('p');
  line.className = 'console-entry';
  line.textContent = text;
  log.appendChild(line);
  log.scrollTop = log.scrollHeight;
}

/**
 * Shows a typing animation modal with a message
 * @param {string} message - The message to display with typing effect
 */
function showTypingModal(message) {
  const modal = document.querySelector('.typing-modal');
  const overlay = document.querySelector('.modal-overlay');
  const content = modal.querySelector('.modal-content');
  
  modal.classList.add('active');
  overlay.style.display = 'block';
  
  let currentText = '';
  let currentIndex = 0;
  
  function typeNextChar() {
    if (currentIndex < message.length) {
      currentText += message[currentIndex];
      content.innerHTML = currentText + '<span class="typing-cursor"></span>';
      currentIndex++;
      setTimeout(typeNextChar, 100);
    } else {
      setTimeout(() => {
        modal.classList.remove('active');
        overlay.style.display = 'none';
      }, 2000);
    }
  }
  
  typeNextChar();
}

/**
 * Mints a character that was previously saved (for wallet restoration)
 * @param {Object} w - Worker data object containing name, position, and message
 */
function mintRestoredCharacter(w) {
  const chara = new Character(w.name, w.x, w.y, w.direction, MOVE, w.message);
  
  if (Character.images[w.name].complete) {
    map.addChara(chara);
  } else {
    Character.images[w.name].onload = function() {
      map.addChara(chara);
    };
  }

  createNPCButton(w.name, w.message);
  npcCounter++;
  mintedWorkers[w.name] = true;
}

// ============================================================================
// EXPOSE FUNCTIONS TO WINDOW (Required for wallet.js and other modules)
// ============================================================================
window.logToConsole = logToConsole;
window.showTypingModal = showTypingModal;
window.loadWorkersForUser = loadWorkersForUser;
window.mintRestoredCharacter = mintRestoredCharacter;

// ============================================================================
// INITIALIZATION
// ============================================================================
window.addEventListener('DOMContentLoaded', () => {
  map = new Map("test");
  calcOffset();
  setInterval(mainLoop, 60);
  
  // Setup event listeners
  document.getElementById('connectWalletBtn').addEventListener('click', connectWallet);
  document.getElementById('mintBtn').addEventListener('click', mintCharacter);
  document.getElementById('canvas').addEventListener('click', handleCanvasClick);
  
  logToConsole("🎮 Mini Agents $MINI initialized successfully!");
  logToConsole("🔗 Connect your wallet to start minting agents.");
});

// ============================================================================
// GAME LOOP
// ============================================================================
function mainLoop() {
  const ctx = document.getElementById('canvas').getContext('2d');
  ctx.clearRect(0, 0, WIDTH, HEIGHT);
  map.update();
  map.draw(ctx, offset);
}

// ============================================================================
// CHARACTER MINTING
// ============================================================================

/**
 * Mints a new random AI agent character
 * Requires wallet connection and checks for available agent types
 */
function mintCharacter() {
  // Check wallet connection
  if (!userWallet) {
    logToConsole("❌ Connect your wallet before minting an agent.");
    return;
  }

  // Check if all agents have been minted
  if (Object.values(mintedWorkers).every(minted => minted)) {
    logToConsole("❌ You are an OG! All agent types have been minted!");
    logToConsole("🎉 Congratulations on collecting them all!");
    return;
  }

  // Get available agent types
  const availableTypes = Object.entries(mintedWorkers)
    .filter(([_, minted]) => !minted)
    .map(([type]) => type);

  // Select random agent
  const randomName = availableTypes[Math.floor(Math.random() * availableTypes.length)];
  const x = Math.floor(Math.random() * map.col);
  const y = Math.floor(Math.random() * map.row);
  
  // Validate position
  if (!map.isMovable(x, y)) return;

  const direction = Math.floor(Math.random() * 4);
  
  // ============================================================================
  // AGENT MESSAGES
  // Add your custom agent message here:
  // your_agent: "Your custom agent introduction message!"
  // ============================================================================
  const messages = {
    inspector: "I am your Inspector, ready to track wallet movements!",
    soldier: "I am your Balance Agent, ready to report!",
    minister: "I am your News Agent, ready to inform!",
    phin: "HI, I AM PHIN, THE REAL OG OF BASE CHAIN",
    abster: "HI, I AM ABSTER, ONE OF THE MOST POPULAR TOKENS IN BASE CHAIN. THIS IS MY CONTRACT: 0x9cfe7b403f76f7d2631ef83331aaa93707217271",
    guru: "I am the Gas Guru, ready to enlighten you about network fees!",
    analyzer: "I am the AI Token Scan, ready to analyze smart contracts!",
    rick: "Wubba Lubba Dub Dub! *burp* Ready to science the sh*t out of crypto!",
    rose: "Hello! I am Rose, your AI companion. Click me to chat!",
    cygaar: "I am Cygaar, ready to inspire you with crypto wisdom! @0xCygaar"
    // Add your custom agent message here:
    // your_agent: "Your custom agent introduction message!"
  };
  
  const message = messages[randomName];
  const chara = new Character(randomName, x, y, direction, MOVE, message);
  
  // Add character to map
  if (Character.images[randomName].complete) {
    map.addChara(chara);
  } else {
    Character.images[randomName].onload = function() {
      map.addChara(chara);
    };
  }

  // Mark as minted
  mintedWorkers[randomName] = true;
  
  // Save to storage
  const characterWallet = generateMockWalletAddress();
  const characterData = {
    name: randomName,
    x,
    y,
    direction,
    message,
    wallet: characterWallet,
  };

  if (userWallet) {
    saveWorkersForUser(userWallet, [
      ...loadWorkersForUser(userWallet),
      characterData
    ]);
  }

  // Create UI button
  createNPCButton(randomName, message);
  npcCounter++;
  logToConsole(`✅ Minted new ${getDisplayName(randomName)}!`);
  logToConsole(`🎯 Agent spawned at position (${x}, ${y})`);
}

/**
 * Creates a button in the NPC list for an agent
 * @param {string} name - Agent internal name
 * @param {string} message - Agent's introduction message
 */
function createNPCButton(name, message) {
  const npcList = document.getElementById('npcList');
  const button = document.createElement('button');
  button.className = 'btn-npc';
  button.innerText = `${getDisplayName(name)} #${npcCounter + 1}`;
  button.addEventListener('click', () => handleAgentClick(name, message));
  npcList.appendChild(button);
}

// ============================================================================
// AGENT INTERACTION HANDLERS
// ============================================================================

/**
 * Handles click events on agents
 * Routes to the appropriate agent handler function
 * @param {string} name - Agent internal name
 * @param {string} message - Agent's message (fallback if no handler exists)
 */
function handleAgentClick(name, message) {
  // ============================================================================
  // AGENT HANDLERS MAP
  // Add your custom agent handler here:
  // your_agent: handleYourAgentMessage
  // If your agent needs wallet: your_agent: () => handleYourAgentMessage(userWallet)
  // ============================================================================
  const handlers = {
    cygaar: handleCygaarMessage,
    soldier: () => handleSoldierMessage(userWallet),
    minister: handleMinisterMessage,
    inspector: () => handleInspectorMessage(userWallet),
    phin: handlePhinMessage,
    abster: handleAbsterMessage,
    guru: handleGuruMessage,
    analyzer: handleAnalyzerMessage,
    rick: handleRickMessage,
    rose: handleRoseMessage
    // Add your custom agent handler here:
    // your_agent: handleYourAgentMessage
    // If needs wallet: your_agent: () => handleYourAgentMessage(userWallet)
  };

  if (handlers[name]) {
    logToConsole(`🎮 Activating ${getDisplayName(name)}...`);
    handlers[name]();
  } else {
    logToConsole(`💬 ${getDisplayName(name)}: ${message}`);
  }
}

/**
 * Handles canvas click events to detect character clicks
 * @param {MouseEvent} e - Click event
 */
function handleCanvasClick(e) {
  const canvas = document.getElementById('canvas');
  const rect = canvas.getBoundingClientRect();
  const scaleX = canvas.width / rect.width;
  const scaleY = canvas.height / rect.height;
  const clickX = (e.clientX - rect.left) * scaleX;
  const clickY = (e.clientY - rect.top) * scaleY;
  const tileX = div(clickX + offset[0], GS);
  const tileY = div(clickY + offset[1], GS);

  // Check if clicked on a character
  for (let chara of map.charas) {
    if (chara.x === tileX && chara.y === tileY) {
      handleAgentClick(chara.name, chara.message);
      break;
    }
  }
}

// ============================================================================
// DISPLAY NAME MAPPING
// ============================================================================

/**
 * Converts internal agent names to display names
 * @param {string} name - Internal agent name
 * @returns {string} Display name for UI
 */
function getDisplayName(name) {
  // ============================================================================
  // AGENT DISPLAY NAMES
  // Add your custom agent display name here:
  // your_agent: "YOUR AGENT NAME"
  // ============================================================================
  const names = {
    soldier: "BALANCE AGENT",
    minister: "NEWS AGENT",
    inspector: "WALLET INSPECTOR",
    phin: "PHIN",
    abster: "ABSTER",
    guru: "GAS GURU",
    analyzer: "AI TOKEN SCAN",
    rick: "RICK",
    rose: "ROSE",
    cygaar: "CYGAAR"
    // Add your custom agent display name here:
    // your_agent: "YOUR AGENT NAME"
  };
  return names[name] || name.toUpperCase();
}

// ============================================================================
// EXPORTS
// ============================================================================
export { 
  map, 
  offset, 
  npcCounter, 
  mintedWorkers, 
  logToConsole, 
  showTypingModal 
};